import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import cx from "classnames";
import { useTranslation } from "react-i18next";
import { Card, Col, Input, message, Switch, Tag } from "antd";
import Table from "app/components/Table/Table";
import Button from "app/components/Button/Button";
import styles from "./TestTypesForm.module.scss";
import { useFetchPaginatedData, useSaveData } from "app/hooks/hooks";
import {
  SiteDTO,
  ListWrapperTestTypeDTO,
  TestTypesApiV2TestTypesGetRequest,
  TestTypeDTO,
  TestTypeCategory,
  SiteTestSiteDTO,
  MeasurementMethod,
} from "@generated/v2";
import { SitesApiV2SitesSaveRequest, sitesSave } from "app/api/v2/sitesApi";
import { testTypesGet } from "app/api/v2/testTypesApi";
import ErrorFallbackUI from "app/components/ErrorFallbackUI";

import _isEmpty from "lodash/isEmpty";
import _camelCase from "lodash/camelCase";
import Row from "app/components/Common/Row";
import Icon from "app/components/Icon/Icon";
import useDebounced from "app/hooks/useDebounced";
import { TableProps, TableRowSelection } from "antd/lib/table";

interface IProps {
  /** Site data */
  site: SiteDTO;
}

type AvailableTestTypesType = Pick<
  SiteTestSiteDTO,
  "testTypeId" | "qrActive"
>[];

const TABLE_SCROLL = { y: 300, x: 800 };

const TestTypesForm = ({ site }: IProps) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");

  const debouncedSearch = useDebounced(search, 500);

  const [availableTestTypes, setAvailableTestTypes] =
    useState<AvailableTestTypesType>(
      site.availableTestTypes?.map(({ testTypeId, qrActive }) => ({
        testTypeId,
        qrActive,
      })) ?? []
    );

  /**
   * Save data hook. Automatically toggles between add and update,
   * depending on whether an id is applied.
   */
  const { loading, isSuccess, isError, setBody } = useSaveData<
    SiteDTO,
    SitesApiV2SitesSaveRequest
  >(sitesSave);

  const { data: testTypes, loading: testTypesLoading } = useFetchPaginatedData<
    ListWrapperTestTypeDTO,
    TestTypesApiV2TestTypesGetRequest
  >(testTypesGet, {
    params: { pageSize: 200 },
  });

  // Handlers

  const handleSelectedRowKeysChange = useCallback(
    (selectedRowKeys: number[] | string[]) => {
      setAvailableTestTypes(prev => {
        const newAvailableTestTypes: AvailableTestTypesType = [];
        selectedRowKeys.forEach((id: string | number) => {
          const type = prev.find(testType => testType.testTypeId === id);
          newAvailableTestTypes.push(type ?? { testTypeId: id as number });
        });
        return newAvailableTestTypes;
      });
    },
    []
  );

  const handleQrSwitchChange = useCallback(
    (qrActive: boolean, testTypeId: number) => {
      setAvailableTestTypes(prev => {
        const newAvailableTestTypes = prev.map(testType => {
          if (testType.testTypeId === testTypeId) {
            return { ...testType, qrActive };
          }
          return testType;
        });
        return newAvailableTestTypes;
      });
    },
    []
  );

  const handleSubmit = () => {
    const { name, customerId, id } = site;

    setBody({
      updateSiteCommand: {
        name,
        availableTestTypes: availableTestTypes.map(testType => ({
          testTypeId: testType.testTypeId,
          qrActive: testType.qrActive,
        })),
        customerId,
      },
      id, // Add an ID, indicating an edit session
    });
  };

  // Effects

  /**
   * Listen for the state of the save data hook, and display status
   * message, and closes on success.
   */
  useEffect(() => {
    if (isSuccess) {
      message.success(t("sites.testTypesSaveSuccess"));
    } else if (isError) {
      message.error(t("sites.testTypesSaveError"));
    }
  }, [isSuccess, isError, t]);

  const filteredTestTypes = useMemo(() => {
    const searchTerm = debouncedSearch.toLowerCase().trim();
    if (!searchTerm) {
      return testTypes;
    }

    const search = (value: string | undefined) => {
      if (!value) {
        return false;
      }

      return value.toLowerCase().trim().includes(searchTerm);
    };

    return testTypes.filter(testType => {
      return (
        search(testType.name) ||
        search(testType.brand) ||
        search(t(`categoryEnum.${_camelCase(testType.category)}`)) ||
        search(t(`measurementMethod.${_camelCase(testType.measurementMethod)}`))
      );
    });
  }, [debouncedSearch, t, testTypes]);

  const rawSelection = useMemo<TableRowSelection<TestTypeDTO>>(
    () => ({
      selectedRowKeys: availableTestTypes.map(
        ({ testTypeId }) => testTypeId as number
      ),
      onChange: handleSelectedRowKeysChange,
    }),
    [availableTestTypes, handleSelectedRowKeysChange]
  );

  const columns = useMemo<TableProps<TestTypeDTO>["columns"]>(
    () => [
      {
        title: t("testItems.columnName"),
        key: "name",
        dataIndex: "name",
      },
      {
        title: t("pageFilter.measurementMethod"),
        key: "measurementMethod",
        dataIndex: "measurementMethod",
        render: (text: string) => (
          <Tag
            className={cx(
              styles.tag,
              MeasurementMethod.Quantitative === text
                ? styles.quantitativeTag
                : styles.qualitativeTag
            )}
          >
            {t(`measurementMethod.${_camelCase(text)}`)}
          </Tag>
        ),
      },
      {
        title: t("pageFilter.testTypeBrand"),
        key: "brand",
        dataIndex: "brand",
      },
      {
        title: t("testItems.columnCategory"),
        key: "category",
        dataIndex: "category",
        render: (text: string) => t(`categoryEnum.${_camelCase(text)}`),
      },
      {
        title: t("testItems.columnQrCode"),
        key: "status",
        render: (_, { category, id, measurementMethod }: TestTypeDTO) =>
          category === TestTypeCategory.Strip ? (
            t("default.notApplicable")
          ) : (
            <Switch
              checkedChildren={<Icon type="check" />}
              unCheckedChildren={<Icon type="close" />}
              checked={
                MeasurementMethod.Quantitative === measurementMethod
                  ? true
                  : !!availableTestTypes?.find(
                      testType => testType.testTypeId === id
                    )?.qrActive
              }
              onChange={checked => handleQrSwitchChange(checked, id as number)}
              disabled={
                MeasurementMethod.Quantitative === measurementMethod
                  ? true
                  : !availableTestTypes.find(
                      ({ testTypeId }) => testTypeId === id
                    )
              }
            />
          ),
      },
    ],
    [availableTestTypes, handleQrSwitchChange, t]
  );

  return (
    <ErrorFallbackUI>
      <Card
        loading={testTypesLoading}
        title={
          <Header
            isEmpty={_isEmpty(availableTestTypes)}
            search={search}
            onSearch={setSearch}
          />
        }
        extra={
          <Button
            icon="save"
            onClick={handleSubmit}
            type="primary"
            loading={loading}
            disabled={_isEmpty(availableTestTypes)}
          >
            {t("sites.buttonSaveTestTypes")}
          </Button>
        }
        className={cx(styles.card, {
          [styles.error]: _isEmpty(availableTestTypes),
        })}
      >
        <Table
          scroll={TABLE_SCROLL}
          dataSource={filteredTestTypes}
          loading={loading}
          rowSelection={rawSelection}
          pagination={false}
          columns={columns}
        />
      </Card>
    </ErrorFallbackUI>
  );
};

function Header({
  search,
  isEmpty,
  onSearch,
}: {
  search: string;
  isEmpty: boolean;
  onSearch: (value: string) => void;
}): React.ReactElement {
  const { t } = useTranslation();

  const onChange: React.ChangeEventHandler<HTMLInputElement> = event => {
    onSearch(event.target.value);
  };

  return (
    <Row align="middle" type="flex" gutter={12}>
      <Col>
        <h3 className={styles.tableTitle}>
          {t("sites.inputTestTypes")}
          {isEmpty && (
            <span className={styles.errorText}>
              {t("sites.inputTestTypesNoneSelected")}
            </span>
          )}
        </h3>
      </Col>
      <Col>
        <Input
          placeholder={t("general.search")}
          size="large"
          suffix={<Icon type="search" className={styles.searchIcon} />}
          onChange={onChange}
          value={search}
        />
      </Col>
    </Row>
  );
}

export default memo(TestTypesForm);
